import { useEffect, useState } from "react";

/**
 * "Add to Home Screen" for a phone using Brocoded in a browser tab. Alerts and the fast launch only work well from the
 * installed app, so the pairing success screen asks for it. On Android it is the last step of pairing (one tap through
 * Chrome's own install prompt, or ⋮ → Add to Home screen where there is none); on iPhone it is a one-line hint. Nothing is
 * shown inside the installed app, the Android app, or after the hint was dismissed. Android shares storage between Chrome
 * and the installed app, so a pairing made in the tab is already there when the app opens.
 */
const KEY = "brocoded.install-hint-dismissed";
type Prompt = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };
let deferred: Prompt | null = null;
let justInstalled = false;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((f) => f());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // keep it for our own button instead of the browser's mini-bar
    deferred = e as Prompt;
    changed();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    justInstalled = true;
    changed();
  });
}

export const installedApp = (): boolean => installed();
/** The Home Screen app on an iPhone or iPad: it can't show Google's accounts, so sign-in goes through the browser. */
export const iosHomeScreenApp = (): boolean => {
  const ua = navigator.userAgent;
  return installed() && !(window as any).Capacitor?.isNativePlatform?.() && (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1));
};
/** An iPhone or iPad browser tab (not the Home Screen app): what a later "Add to Home Screen" will start from. */
export const iosBrowserTab = (): boolean => {
  const ua = navigator.userAgent;
  return !installed() && (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1));
};
const installed = (): boolean => {
  const cap = (window as any).Capacitor;
  return !!cap?.isNativePlatform?.() || window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;
};

/**
 * ios: Share → Add to Home Screen. prompt: Chrome can install it in one tap. android: no prompt from the browser, so say
 * where its menu item is. added: installed just now from this tab.
 */
export type InstallHint = { kind: "ios" } | { kind: "prompt" } | { kind: "android" } | { kind: "added" } | null;

export function installHint(): InstallHint {
  if (justInstalled) return { kind: "added" };
  try {
    if (installed() || localStorage.getItem(KEY)) return null;
  } catch {
    if (installed()) return null;
  }
  if (deferred) return { kind: "prompt" };
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return { kind: "android" };
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return ios ? { kind: "ios" } : null;
}

/** The hint, kept current as the browser offers its install prompt or finishes installing. */
export function useInstallHint(): [InstallHint, () => void] {
  const [hint, setHint] = useState(installHint);
  useEffect(() => {
    const f = () => setHint(installHint());
    listeners.add(f);
    f();
    return () => void listeners.delete(f);
  }, []);
  return [hint, () => setHint(installHint())];
}

/** Show the browser's install dialog. True when the person accepted it. */
export async function runInstall(): Promise<boolean> {
  const d = deferred;
  if (!d) return false;
  deferred = null;
  await d.prompt();
  const { outcome } = await d.userChoice;
  if (outcome === "accepted") justInstalled = true; // `appinstalled` follows, but the screen can say so now
  changed();
  return outcome === "accepted";
}

export function dismissInstallHint(): void {
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    /* shown again next time: fine */
  }
  changed();
}
